import React, { useState, useEffect, useCallback, useRef } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  Spell,
  SpellLimitation,
  // SpellList
} from "@dndModels";
import { 
  DisablableOption 
} from "@models";
// import { CastingTime, SpellLevel } from '../../models/Types';

import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBox";
// import DisplayObjects from '../display/DisplayObjects';

interface Props {
  name: string;
  value?: string;
  values?: string[];
  filter?: any;
  multiple?: boolean;
  level?: number;
  spell_limit?: SpellLimitation;
  extra_options?: string[];
  character?: Character;
  ignore_limits_for?: string[];
  color?: string;
  onChange?: Function | null;
  max_selected?: number;
  onFilterChange?: (new_filter: any) => void;
  disabled?: boolean;
  log_it?: boolean;
  hide_if_only_1?: boolean;
  onChangeWithLevels?: ((spells: { [level: number]: string[] }) => void) | null;
  extra_filter?: (o: Spell) => boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
// At module level, outside the component
const DEFAULT_VALUES: string[] = [];
const DEFAULT_FILTER: any = {};

export default function SelectSpellBox({
  name,
  level = -1,
  spell_limit = new SpellLimitation(),
  character,
  ignore_limits_for = [],
  color = "",
  disabled = false,
  log_it = false,
  value = "",
  values = DEFAULT_VALUES,
  filter = DEFAULT_FILTER,
  multiple = false,
  hide_if_only_1 = false,
  max_selected = -1,
  onChange = null,
  onChangeWithLevels = null,
  onFilterChange,
  extra_filter,
  extra_options
}: Props) {

  // const renderCount = useRef(0);
  // const prevDeps = useRef({ filter, value, values, spell_limit });

  // useEffect(() => {
  //   renderCount.current += 1;
  //   const prev = prevDeps.current;
  //   console.log(`SelectSpellBox render #${renderCount.current}`, {
  //     filter_changed: prev.filter !== filter,
  //     value_changed: prev.value !== value,
  //     values_changed: prev.values !== values,
  //     spell_limit_changed: prev.spell_limit !== spell_limit,
  //   });
  //   prevDeps.current = { filter, value, values, spell_limit };
  // });

  const use_level = level > -1 ? level : -1;
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [reloading, setReloading] = useState(false);
  const [true_filter, setTrueFilter] = useState<any>(use_level > -1 ? { ...filter, level: { "$in": [use_level] } } : {...filter});
  const [my_filter, setFilter] = useState<any>(use_level > -1 ? { ...filter, level: [use_level] } : {...filter});
  const [hiding, setHiding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["spell"]});
    if (log_it) {
      console.log(res);
    }
    // debugger;
    const the_spells = res.spell as Spell[];
    // const spell_finder = the_spells.find(o => o._id === value);
    // setSpellLists(res.spell_list);
    setSpells(the_spells);
    const new_filter = {...my_filter,...filter}; // ,...old_filter};
    const new_true_filter = {...new_filter};
    if (new_filter.effect_types) {
      new_filter.effect_type = new_filter.effect_types;
      delete new_filter.effect_types;
    }
    if (new_filter.attack_types) {
      new_filter.attack_type = new_filter.attack_types;
      delete new_filter.attack_types;
    }
    if (new_filter.target_types) {
      new_filter.target_type = new_filter.target_types;
      delete new_filter.target_types;
    }
    if (new_filter.spell_list_ids) {
      new_filter.spell_list = new_filter.spell_list_ids;
      delete new_filter.spell_list_ids;
    }
    if (new_filter.level) {
      if (new_filter.level["in"]) {
        new_filter.level["in"] = new_filter.level["in"].map((o: any) => `${o}`);
      } else if (new_filter.level["nin"]) {
        new_filter.level["nin"] = new_filter.level["nin"].map((o: any) => `${o}`);
      }
      new_filter.spell_level = new_filter.level;
      delete new_filter.level;
    }
    if (value !== "" || values.length > 0) {
      // console.log(the_spells);
      let filtered = api.filter("spell", the_spells, new_true_filter);
      // console.log(filtered);
      if (!spell_limit.isEmpty()) {
        // console.log(spell_limit);
        if (character) {
          filtered = filtered.filter(o => 
            ignore_limits_for.includes(o._id) || 
            char_util.fitCharSpellLimit2(character, o, spell_limit) // , true)
          );
        } else {
          filtered = filtered.filter(o => ignore_limits_for.includes(o._id) || api.fitSpellLimit({ spell: o, limit: spell_limit}));
        }
      }
      // console.log(filtered);
      filtered = api.filter("spell", filtered, new_filter);
      // console.log({...new_true_filter});
      // console.log({...new_filter});
      // console.log(filtered);
      if (filtered.length > 100) {
        new_filter.on_list = "True";
      }
      // debugger;
      if (hide_if_only_1 && filtered.length === 1) {
        setHiding(true);
      }
    }
    setFilter(new_filter);
    updateFilter(new_true_filter, true);
    
    setReloading(true);
    setLoading(false);
  }, [filter, value, values]);

  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setTimeout(() => { loadRef.current(); }, 1000);
    }
  }, [fetched]); // load no longer a dep — only runs when fetched resets

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const updateFilter = (changed: any, loading = false) => {
    const new_filter = {...filter,...changed};
    if (new_filter.effect_type) {
      new_filter.effect_types = new_filter.effect_type;
      delete new_filter.effect_type;
    }
    if (new_filter.attack_type) {
      new_filter.attack_types = new_filter.attack_type;
      delete new_filter.attack_type;
    }
    if (new_filter.target_type) {
      new_filter.target_types = new_filter.target_type;
      delete new_filter.target_type;
    }
    if (new_filter.spell_list) {
      new_filter.spell_list_ids = new_filter.spell_list;
      delete new_filter.spell_list;
    }
    if (new_filter.spell_level) {
      if (new_filter.spell_level["in"]) {
        new_filter.spell_level["in"] = new_filter.spell_level["in"].map((o: any) => { return o === "Cantrip" ? 0 : +o; });
      } else if (new_filter.spell_level["nin"]) {
        new_filter.spell_level["nin"] = new_filter.spell_level["nin"].map((o: any) => { return o === "Cantrip" ? 0 : +o; });
      }
      new_filter.level = new_filter.spell_level;
      delete new_filter.spell_level;
    }
    if (new_filter.on_list !== undefined) {
      delete new_filter.on_list;
    }

    setTrueFilter(new_filter);
    setReloading(true);
    if (!loading && onFilterChange) {
      onFilterChange(new_filter);
    }
  }

  const updateFilters = (changed: any, loading = false) => {
    const new_filter = {...changed};
    // console.log(my_filter);
    // console.log(changed);
    if (new_filter.effect_type) {
      new_filter.effect_types = new_filter.effect_type;
      delete new_filter.effect_type;
    }
    if (new_filter.attack_type) {
      new_filter.attack_types = new_filter.attack_type;
      delete new_filter.attack_type;
    }
    if (new_filter.target_type) {
      new_filter.target_types = new_filter.target_type;
      delete new_filter.target_type;
    }
    if (new_filter.spell_list) {
      new_filter.spell_list_ids = new_filter.spell_list;
      delete new_filter.spell_list;
    }
    if (new_filter.spell_level) {
      if (new_filter.spell_level["in"]) {
        new_filter.spell_level["in"] = new_filter.spell_level["in"].map((o: any) => { return o === "Cantrip" ? 0 : +o; });
      } else if (new_filter.spell_level["nin"]) {
        new_filter.spell_level["nin"] = new_filter.spell_level["nin"].map((o: any) => { return o === "Cantrip" ? 0 : +o; });
      }
      new_filter.level = new_filter.spell_level;
      delete new_filter.spell_level;
    }
    // console.log(new_filter);

    setFilter(new_filter);
    updateFilter(changed, loading);
  }

  const get_filtered = () => {
    let filtered = [...spells];
    // debugger;
    
    const use_level = level > -1 ? level : -1;
    // if (ignore_limits_for.length > 0) {
    //   debugger;
    // }
    if (use_level > -1) {
      filtered = filtered.filter(o => o.level === use_level);
    }
    if (log_it) {
      console.log(filtered);
    }
    if (!spell_limit.isEmpty()) {
      // console.log(spell_limit);
      if (character) {
        filtered = filtered.filter(o => 
          ignore_limits_for.includes(o._id) || 
          char_util.fitCharSpellLimit2(character, o, spell_limit) // , true)
        );
      } else {
        filtered = filtered.filter(o => ignore_limits_for.includes(o._id) || api.fitSpellLimit({ spell: o, limit: spell_limit }));
      }
    }
    if (log_it) {
      console.log(filtered);
    }
    if (character) {
      const sb_ids = character.c_options.source_books;
      filtered = filtered.filter(o => common_util.anyOverlaps(sb_ids, o.source_book_ids));
      // const blocked_spells = character.c_options.blocked_spells;
      // filtered = filtered.filter(o => !blocked_spells.includes(o._id));
    }
    if (log_it) {
      console.log(filtered);
    }
    const use_filter = {...my_filter};
    if (use_filter.on_list !== undefined) {
      if (use_filter.on_list) {
        filtered = filtered.filter(o => value === o._id || values.includes(o._id));
      } else {
        filtered = filtered.filter(o => value !== o._id && !values.includes(o._id));
      }
      delete use_filter.on_list;
    }
    filtered = api.filter("spell", filtered, true_filter) as Spell[];
    filtered = api.filter("spell", filtered, use_filter) as Spell[];
    if (log_it) {
      console.log(filtered);
    }
    if (extra_filter) {
      filtered = filtered.filter(o => extra_filter(o));
    }
    if (log_it) {
      console.log(filtered);
    }
    return filtered.map(o => {
      const option: DisablableOption = {
        id: o._id, text: o.true_name
      };
      return option;
    });
  }

  // console.log("Rendering Select Spell Box");
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const filtered = get_filtered();
    if (log_it) {
      console.log(filtered);
    }
    if (hiding && filtered.length === 1) {
      return <></>;
    } else {
      return (
        <Grid container spacing={0} direction="row" style={{
          border: (color === "" ? "" : "1px solid #1C9AEF")
        }}>
          <Grid
            size={{
              xs: 12
            }}>
            <FilterBox 
              data_type="spell"
              extra_fields={["on_list"]}
              quick_fields={["on_list"]}
              flat={onFilterChange === undefined}
              dont_use_api
              the_filter={my_filter}
              all_objects={spells}
              onChange={updateFilters}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            { reloading ?
              (<LoadingBox />)
            : filtered.length > 100 ?
              <span>Too many spells ({ filtered.length })</span>
            : multiple ?
              <SelectObjectBox 
                options={filtered}
                values={values} 
                multiple
                name={name}
                ignore_missing
                max_selected={max_selected}
                disabled={disabled}
                extra_options={extra_options}
                // renderValues={ renderValues() }
                onChange={(ids: string[]) => {
                  if (onChange) {
                    onChange(ids);
                  }
                  if (onChangeWithLevels) {
                    const spells_by_level: { [level: number]: string[] } = {};
                    const these_spells = spells.filter(o => ids.includes(o._id));
                    for (let i = 0; i < 10; ++i) {
                      const spell_ids = these_spells.filter(o => o.level === i).map(o => o._id);
                      if (spell_ids.length > 0) {
                        spells_by_level[i] = spell_ids;
                      }
                    }
                    onChangeWithLevels(spells_by_level);
                  }
                }}
              />
            :
              <SelectObjectBox 
                options={filtered}
                value={value} 
                name={name}
                disabled={disabled}
                extra_options={extra_options}
                onChange={(id: string) => {
                  const objFinder = spells.find(o => o._id === id);
                  if (objFinder) {
                    if (onChange) {
                      onChange(objFinder._id);
                    }
                    if (onChangeWithLevels) {
                      const spells_by_level: { [level: number]: string[] } = {};
                      const this_spell = spells.find(o => value === o._id);
                      if (this_spell) {
                        spells_by_level[this_spell.level] = [this_spell._id];
                      }
                      onChangeWithLevels(spells_by_level);
                    }
                  }
                }}
              />
            }
          </Grid>
        </Grid>
      );
    }
  }
}
